import React from 'react';
import { Flame, Zap, Clock } from 'lucide-react';
import { evaluateUrgency } from '@/utils/urgencyEngine';
import { Badge } from '@/components/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

export interface UrgencyBadgeProps {
  priority?: string | null;
  deadline?: string | Date | null;
  status?: string | null;
  showIconOnly?: boolean;
  showIcon?: boolean;
  className?: string;
}

export const UrgencyBadge: React.FC<UrgencyBadgeProps> = ({
  priority,
  deadline,
  status,
  showIconOnly = false,
  showIcon = true,
  className,
}) => {
  const evalResult = evaluateUrgency({ priority, deadline, status });

  // Icon element based on type
  const renderIcon = () => {
    if (!showIcon) return null;
    switch (evalResult.iconType) {
      case 'flame':
        return (
          <Flame
            className={cn(
              'h-3.5 w-3.5 shrink-0',
              evalResult.level === 'CRITICAL_URGENT' ? 'text-white' : 'text-rose-600',
              evalResult.isUrgent && 'animate-pulse'
            )}
            data-testid="urgency-icon-flame"
          />
        );
      case 'zap':
        return (
          <Zap
            className="h-3.5 w-3.5 shrink-0 text-amber-600"
            data-testid="urgency-icon-zap"
          />
        );
      case 'clock':
        return (
          <Clock
            className="h-3.5 w-3.5 shrink-0 text-blue-500"
            data-testid="urgency-icon-clock"
          />
        );
      default:
        return null;
    }
  };

  const badgeContent = (
    <Badge
      variant="outline"
      className={cn(
        'inline-flex items-center gap-1.5 px-2 py-0.5 text-xs transition-colors',
        evalResult.badgeColor,
        className
      )}
      data-testid="urgency-badge"
      data-urgency-level={evalResult.level}
    >
      {renderIcon()}
      {!showIconOnly && <span>{evalResult.badgeLabel}</span>}
    </Badge>
  );

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>{badgeContent}</TooltipTrigger>
        <TooltipContent side="top" className="text-xs max-w-xs font-normal">
          <p>{evalResult.tooltipText}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};
